import { Package, Plus, Minus, Check } from 'lucide-react';
import { cn, formatPrice } from '@/lib/utils';
import type { ExperienceDetail } from '@/lib/api';
import type { BookingData, SelectedAddOn } from './BookingFlow';

interface AddOnsStepProps {
  experience: ExperienceDetail;
  bookingData: BookingData;
  onUpdate: (data: Partial<BookingData>) => void;
  onNext: () => void;
  onBack: () => void;
}

export function AddOnsStep({ experience, bookingData, onUpdate, onNext, onBack }: AddOnsStepProps) {
  const addOns = experience.addOns || [];
  const totalGuests = bookingData.adultCount + bookingData.childCount;

  const getSelectedAddOn = (id: string): SelectedAddOn | undefined => {
    return bookingData.addOns.find(a => a.id === id);
  };

  const handleToggleAddOn = (addOn: ExperienceDetail['addOns'][0]) => {
    const existing = getSelectedAddOn(addOn.id);
    
    if (existing) {
      onUpdate({ addOns: bookingData.addOns.filter(a => a.id !== addOn.id) });
    } else {
      onUpdate({
        addOns: [...bookingData.addOns, {
          id: addOn.id,
          name: addOn.name,
          price: addOn.price,
          priceType: addOn.priceType,
          quantity: 1,
        }],
      });
    }
  };

  const handleQuantityChange = (addOnId: string, delta: number) => {
    const existing = getSelectedAddOn(addOnId);
    if (!existing) return;

    const newQuantity = existing.quantity + delta;
    
    // Get max quantity from add-on config (default to 10 if not specified)
    const addOnConfig = addOns.find(a => a.id === addOnId);
    const maxQuantity = addOnConfig?.maxQuantity || 10;
    
    if (newQuantity <= 0) {
      onUpdate({ addOns: bookingData.addOns.filter(a => a.id !== addOnId) });
    } else if (newQuantity <= maxQuantity) {
      onUpdate({
        addOns: bookingData.addOns.map(a =>
          a.id === addOnId ? { ...a, quantity: newQuantity } : a
        ),
      });
    }
    // If newQuantity > maxQuantity, don't update (button should be disabled)
  };

  const calculateAddOnPrice = (addOn: SelectedAddOn): number => {
    if (addOn.priceType === 'per_person') {
      return addOn.price * addOn.quantity * totalGuests;
    }
    return addOn.price * addOn.quantity;
  };

  const totalAddOnsPrice = bookingData.addOns.reduce(
    (sum, addOn) => sum + calculateAddOnPrice(addOn),
    0
  );

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold">Enhance Your Experience</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Add optional extras to make your experience even better
        </p>
      </div>

      {/* Add-ons List */}
      <div className="space-y-3">
        {addOns.map((addOn) => {
          const selected = getSelectedAddOn(addOn.id);
          const isSelected = !!selected;

          return (
            <div
              key={addOn.id}
              className={cn(
                'rounded-lg border p-4 transition-all cursor-pointer',
                isSelected ? 'border-primary bg-primary/5' : 'hover:border-muted-foreground/30'
              )}
              onClick={() => !isSelected && handleToggleAddOn(addOn)}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Package className="h-5 w-5 text-muted-foreground shrink-0" />
                    <h3 className="font-medium">{addOn.name}</h3>
                    {isSelected && (
                      <Check className="h-4 w-4 text-primary shrink-0" />
                    )}
                  </div>
                  {addOn.description && (
                    <p className="mt-1 text-sm text-muted-foreground line-clamp-2 ml-7">
                      {addOn.description}
                    </p>
                  )}
                  <div className="mt-2 ml-7 flex items-center gap-2 text-sm">
                    <span className="font-semibold">
                      {formatPrice(addOn.price, experience.currency)}
                    </span>
                    <span className="text-muted-foreground">
                      {addOn.priceType === 'per_person' ? 'per person' : 'per booking'}
                    </span>
                  </div>
                </div>

                {/* Quantity Controls */}
                {isSelected && selected && (
                  <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => handleQuantityChange(addOn.id, -1)}
                      className="flex h-8 w-8 items-center justify-center rounded-full border bg-background hover:bg-muted"
                      aria-label="Decrease quantity"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <span className="w-8 text-center font-medium">{selected.quantity}</span>
                    <button
                      onClick={() => handleQuantityChange(addOn.id, 1)}
                      disabled={selected.quantity >= (addOn.maxQuantity || 10)}
                      className="flex h-8 w-8 items-center justify-center rounded-full border bg-background hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                      aria-label="Increase quantity"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                )}

                {!isSelected && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleToggleAddOn(addOn); }}
                    className="rounded-lg border px-3 py-1.5 text-sm font-medium hover:bg-muted"
                  >
                    Add
                  </button>
                )}
              </div>

              {/* Price Summary for Selected */}
              {isSelected && selected && (
                <div className="mt-3 ml-7 pt-3 border-t text-sm">
                  <div className="flex justify-between text-muted-foreground">
                    <span>
                      {selected.quantity} × {formatPrice(addOn.price, experience.currency)}
                      {addOn.priceType === 'per_person' && ` × ${totalGuests} guests`}
                    </span>
                    <span className="font-medium text-foreground">
                      {formatPrice(calculateAddOnPrice(selected), experience.currency)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>


      {/* Total Add-ons */}
      {bookingData.addOns.length > 0 && (
        <div className="mt-6 rounded-lg bg-muted/50 p-4">
          <div className="flex justify-between items-center">
            <span className="font-medium">Add-ons Total</span>
            <span className="text-lg font-semibold">
              {formatPrice(totalAddOnsPrice, experience.currency)}
            </span>
          </div>
        </div>
      )}

      {/* Skip option */}
      {addOns.length > 0 && bookingData.addOns.length === 0 && (
        <p className="mt-4 text-center text-sm text-muted-foreground">
          No add-ons selected. You can continue without any extras.
        </p>
      )}

      {/* Navigation */}
      <div className="mt-6 flex justify-between">
        <button
          onClick={onBack}
          className="rounded-lg px-6 py-3 font-medium text-muted-foreground hover:text-foreground"
        >
          Back
        </button>
        <button
          onClick={onNext}
          className={cn(
            'rounded-lg px-6 py-3 font-medium transition-colors',
            'bg-primary text-primary-foreground hover:bg-primary/90'
          )}
        >
          Continue
        </button>
      </div>
    </div>
  );
}
